import React, { useEffect, useState } from 'react';
import StandardModal, { StandardModalBody, StandardModalFooter, useModalLoading } from '../../../components/modales/StandardModal';
import { Typography, Button, Alert, Box } from '@mui/material';
import { LoadingButton } from '@routes-plan/ui';
import { formatCurrency } from '../../../utils/format/currency';
import { Loan } from '../../../models/loan';
import { nowOperationalDateString, nowOperationalDateTimeString } from '../../../utils/operationalDateTime';
import axios from 'axios';
import { useT } from '../../../i18n';
import { dailyLimitMessage } from '../../../utils/dailyLimit';

interface RefinanceModalProps {
  open: boolean;
  onClose: () => void;
  client: Loan | null;
  clientId?: number;
  modalData: {
    loanId: number;
    totalValue: number;
    interestRate: number;
    term: number;
    balance: number;
    refinanceCount: number;
    initialValue: number;
    startDate: string;
    userId: string; // Puedes tiparlo mejor si quieres
  } | null;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  onPaymentSuccess: () => void;

}


// Estilos del modal

const RefinanceModal: React.FC<RefinanceModalProps> = ({ open, onClose, client, clientId, modalData, showAlert, showLoading, hideLoading, onPaymentSuccess }) => {
  const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal estándar.
  const { loading: modalBusy, showLoading: showModalLoading, hideLoading: hideModalLoading } = useModalLoading();
  const [term, setTerm] = useState('');
  const [userId, setUserId] = useState('');
  const [startDate, setStartDate] = useState<string | number>('');
  
  const [refinancedValue, setRefinancedValue] =  useState<string | number>('');
  const [dailyPay, setDailyPay] = useState<string | number>('');
  const [balance, setBalance] = useState<string | number>('');
  const [refinanceCount, setRefinanceCount] = useState<string | number>('');
  
  const [newInterestValue, setNewInterestValue] = useState<string | number>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (modalData) {
      const refinancedValue = Math.ceil(Number(modalData.balance) + Number((modalData.balance * modalData.interestRate) / 100)); // si quieres porcentaje
      const newInterestValue = Number((modalData.balance * modalData.interestRate) / 100)
      const newDailyPay = Math.ceil(Number(refinancedValue / modalData.term));
      const nextRefinanceCount = Number(modalData.refinanceCount + 1)
      
      setRefinancedValue(refinancedValue);
      setTerm(modalData.term.toString());
      setBalance(modalData.balance );

      setNewInterestValue(newInterestValue);
      setDailyPay(newDailyPay);
      setRefinanceCount(nextRefinanceCount);
      setStartDate(modalData.startDate);
      setUserId(modalData.userId);
    }
  }, [modalData]);

  const handleAccept = async () => {
    setIsSaving(true);
    
    const currentDate = nowOperationalDateTimeString();
    //console.log('Refinance Date:', currentDate)
    const formData = {
      value: balance,
      balance: refinancedValue,
      totalValue: refinancedValue,
      installmentValue: dailyPay,     
      projectedPayment: 0,
      date: nowOperationalDateString(),
      interestValue: newInterestValue,
      loanId: Number(clientId),
      initialValue: modalData?.initialValue,
      refinanceDate: currentDate,
      startDate,
      refinanceCount,
      userId
    };
    const url = `/loans/${clientId}/refinance`;
    try {
      showModalLoading();
      const response = await axios.put(url, formData);
      showAlert(t('loanActions.refinanceOk'), 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));  
      setErrorMessage('');
      onPaymentSuccess();
      onClose(); // Cerrar el modal después de guardar
    } catch (error) {
      setErrorMessage(dailyLimitMessage(error, t) ?? t('loanActions.refinanceError'));
    } finally {
      setIsSaving(false);
      hideModalLoading();
    }
  };

  const handleClose = () => {
    setErrorMessage('');
    onClose();
  };

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={t('loanActions.refinanceTitle')}
      subtitle={client?.client?.fullName}
      maxWidth={600}
      loading={modalBusy || isSaving}
      composed
    >
      <StandardModalBody>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('loanActions.refinanceConfirm', { loan: clientId ?? '', client: client?.client?.fullName ?? '', idNumber: client?.client?.idNumber ?? '' })}
        </Typography>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {/* Datos con los que se refinancia (no se editan aquí). */}
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 2, py: 0.5 }}>
          {[
            { label: t('loanActions.refinanceValue'), value: formatCurrency(modalData?.balance ?? 0) },
            { label: t('loanActions.term'), value: modalData?.term ?? '' },
            { label: t('loanActions.utility'), value: modalData?.interestRate != null ? `${modalData.interestRate} %` : '' },
            { label: t('lend.installmentValue'), value: formatCurrency(dailyPay) },
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">{item.label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>{item.value}</Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.25 }}>
            <Typography variant="subtitle2">{t('lend.totalToPay')}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'right' }}>{formatCurrency(refinancedValue)}</Typography>
          </Box>
        </Box>

      </StandardModalBody>
      <StandardModalFooter>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton variant="contained" color="primary" onClick={handleAccept} loading={isSaving}>
          {t('loans.refinance')}
        </LoadingButton>

      </StandardModalFooter>
    </StandardModal>
  );
};

export default RefinanceModal;
